"use client";

// Cookie consent banner. Gates the Meta Pixel entirely and controls
// whether PostHog may store anything on the device — see lib/consent.ts.

import Link from "next/link";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/Button";
import { setConsent, useConsent, useHasHydrated } from "@/lib/consent";

// Read by ResultsActionBar/ResultsSkeleton (bottom offset, below `lg:` where they're fixed)
// and app/layout.tsx's <Toaster> (bottom inset) so neither renders underneath this banner —
// it shares the same viewport-bottom rect they do. Kept on
// documentElement, not a React context, so those consumers don't need to know this
// component exists; they just read a CSS custom property with a `0px` fallback.
export const CONSENT_BANNER_HEIGHT_VAR = "--bsa-consent-banner-height";

export function CookieConsent() {
  const consent = useConsent();
  const bannerRef = useRef<HTMLDivElement>(null);

  // See useHasHydrated's doc comment (lib/consent.ts) — without this, a visitor who already
  // chose months ago would see the banner flash into the DOM on every load before flipping
  // back out once useConsent()'s real stored value is read.
  const hasHydrated = useHasHydrated();

  const visible = hasHydrated && consent === null;

  // Measured, not hardcoded — the copy wraps to two lines on narrow viewports, changing the
  // banner's height. ResizeObserver keeps the published value correct across that reflow and
  // any font-loading shift, not just at mount.
  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty(CONSENT_BANNER_HEIGHT_VAR, "0px");
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        document.documentElement.style.setProperty(
          CONSENT_BANNER_HEIGHT_VAR,
          `${entry.contentRect.height}px`,
        );
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(CONSENT_BANNER_HEIGHT_VAR, "0px");
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface px-4 py-4 shadow-[var(--shadow-md)] sm:px-6"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <p className="text-sm text-text-muted">
          {/* Scoped to exactly what Accept/Reject controls: analytics cookies +
              session recordings (PostHog) and advertising measurement (Meta Pixel). If you
              reject, PostHog still runs but cookie-free — nothing stored on the device —
              so the copy must not promise "no analytics" on Reject. */}
          We use cookies to understand how our site is used, including session recordings,
          and to measure our advertising. If you reject, nothing is stored on your device.
          See our{" "}
          <Link href="/privacy" className="underline underline-offset-4 hover:text-text">
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConsent("rejected")}
          >
            Reject
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setConsent("accepted")}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
